'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type SingleColumnSelectControl,
  type TextControl,
  type SelectControl,
  type NumberControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import type { EChartsOption } from 'echarts';

// Type for pie chart specific control values
export interface PieChartControlValues extends ControlValues {
  [ControlIds.Category]: string;
  [ControlIds.Value]: string;
  [ControlIds.Title]: string;
  [ControlIds.ShowLabels]: 'true' | 'false';
  [ControlIds.LabelPosition]: 'outside' | 'inside';
  [ControlIds.ShowPercentages]: 'true' | 'false';
  [ControlIds.Radius]: number;
}

// Define the controls for the pie chart
export const pieChartControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.Category,
          type: ControlType.SingleColumnSelect,
          label: 'Category column',
          required: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Value,
          type: ControlType.SingleColumnSelect,
          label: 'Value column',
          required: true,
          numericOnly: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Title,
          type: ControlType.Text,
          label: 'Chart title'
        } satisfies TextControl
      ]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.ShowLabels,
          type: ControlType.Select,
          label: 'Show labels',
          options: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ],
          defaultValue: 'true'
        } satisfies SelectControl,
        {
          id: ControlIds.LabelPosition,
          type: ControlType.Select,
          label: 'Label position',
          options: [
            { label: 'Outside', value: 'outside' },
            { label: 'Inside', value: 'inside' }
          ],
          defaultValue: 'outside'
        } satisfies SelectControl,
        {
          id: ControlIds.ShowPercentages,
          type: ControlType.Select,
          label: 'Show percentages',
          options: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ],
          defaultValue: 'true'
        } satisfies SelectControl,
        {
          id: ControlIds.Radius,
          type: ControlType.Number,
          label: 'Chart radius',
          min: 30,
          max: 100,
          step: 1,
          defaultValue: 70
        } satisfies NumberControl
      ]
    }
  ]
};

// Function to get initial config based on data
export function getInitialConfig(data: TableDataJSON): Partial<PieChartControlValues> {
  const numericColumns = data.columns.filter(col => 
    col.values.every(val => !isNaN(Number(val)))
  );
  const nonNumericColumns = data.columns.filter(col => 
    !numericColumns.includes(col)
  );

  return {
    category: nonNumericColumns[0]?.id,
    value: numericColumns[0]?.id,
    title: 'Pie Chart',
    showLabels: 'true',
    labelPosition: 'outside',
    showPercentages: 'true',
    radius: 70
  };
}

// Function to get pie chart configuration
export function getPieChartConfig({
  data,
  title,
  showLabels,
  labelPosition,
  showPercentages,
  radius
}: {
  data: { name: string; value: number }[];
  title: string;
  showLabels: boolean;
  labelPosition: 'outside' | 'inside';
  showPercentages: boolean;
  radius: number;
}): EChartsOption {
  return {
    title: {
      text: title,
      left: 'center'
    },
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c} ({d}%)'
    },
    toolbox: {
      show: true,
      feature: {
        saveAsImage: {
          title: 'Save as Image',
          name: 'pie-chart'
        },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Zoom Reset'
          }
        }
      }
    },
    series: [
      {
        type: 'pie',
        radius: `${radius}%`,
        data,
        label: {
          show: showLabels,
          position: labelPosition,
          formatter: showPercentages ? '{b}: {c} ({d}%)' : '{b}: {c}'
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  };
}

export const pieChartConfig: CalculatorConfig<CalculatorId.PieChart> = {
  id: CalculatorId.PieChart,
  type: CalculatorType.Graph,
  name: 'Pie Chart',
  controls: pieChartControls,
  initialData: TableDataJSON.from({
    columns: [
      { id: 'department', name: 'Department', values: ['Marketing', 'Sales', 'Engineering', 'Support', 'Operations'] },
      { id: 'budget', name: 'Annual Budget', values: ['850000', '750000', '1200000', '400000', '600000'] },
      { id: 'headcount', name: 'Team Size', values: ['12', '15', '25', '8', '10'] }
    ]
  }),
  initialControlValues: {
    category: 'department',
    value: 'budget',
    title: 'Annual Budget by Department',
    showLabels: 'true',
    labelPosition: 'outside',
    showPercentages: 'true',
    radius: 70
  },
  getInitialConfig,
  getVisualizationData: ({ data, controls }) => {
    const pieControls = controls as PieChartControlValues;
    
    if (!data?.columns?.length || !pieControls[ControlIds.Category] || !pieControls[ControlIds.Value]) {
      console.log('Missing data or controls:', { data, controls });
      return null;
    }

    const categoryColumn = data.columns.find(col => col.id === pieControls[ControlIds.Category]);
    const valueColumn = data.columns.find(col => col.id === pieControls[ControlIds.Value]);

    if (!categoryColumn || !valueColumn) {
      console.log('Category or value column not found:', { category: pieControls[ControlIds.Category], value: pieControls[ControlIds.Value] });
      return null;
    }

    // Create data array for pie chart
    const pieData = categoryColumn.values.map((category, index) => ({
      name: category,
      value: Number(valueColumn.values[index]) || 0
    }));

    return getPieChartConfig({
      data: pieData,
      title: pieControls[ControlIds.Title],
      showLabels: pieControls[ControlIds.ShowLabels] === 'true',
      labelPosition: pieControls[ControlIds.LabelPosition],
      showPercentages: pieControls[ControlIds.ShowPercentages] === 'true',
      radius: pieControls[ControlIds.Radius]
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts 
        option={visualizationData} 
        className="h-full w-full" 
        themeName="soft-cosmic-flare"
      />
    ) : null
};